/**
 * Uygulama kabuğu: kenar gezinti (geniş ekran) / "Menü" düğmesi (dar ekran),
 * kalıcı durum satırı ve içerik alanı.
 *
 * MENÜ (kullanıcı, 12.10.2026): dört ana başlık — Ana sayfa, Hesap işlemleri,
 * Raporlar, Ayarlar (kullanıcının sırası). Başlık İÇİNDE sıra kullanım
 * sıklığına göre: en sık olan üstte (gerekçe:
 * `claude/notlar/2026-10-12-menu-ergonomisi.md`).
 *
 * TELEFON (kullanıcı kararı 04.11.2026): üst çubuğun SOL başında "Menü"
 * düğmesi. Alttaki sabit çubuk kalktı: iPhone'da tarayıcının alt araç
 * çubuğuyla çakışıp ekranın ortasında kalıyordu. "Menü" masaüstündeki gruplu
 * menünün AYNISINI üst çubuğun altından ekranı kaplayarak açar; bir sayfaya
 * dokununca, "Kapat"la ya da Esc ile kapanır. Sık sayfalar menünün üstünde.
 * İki görünüm tek listeyi (`NAV_GROUPS`) çizer.
 */

import { useEffect, useRef, useState, type ReactNode } from "react";
import { ROUTE_TITLE, routeHref } from "../lib/router";
import type { Route } from "../lib/router";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { cx } from "../ui/primitives";
import { YedekBandi } from "./YedekBandi";
import { DepolamaUyarisi } from "./DepolamaSorusu";
import { KilitBandi } from "./kilit/KilitBandi";
import { M } from "../dil";
import { DilSecici } from "./DilSecici";
import { tutarlariGizle, useTutarlarGizli } from "../lib/tutarGizleme";
import { useZeminAyarlari } from "../lib/zeminAyarlari";

/** "Tutarları gizle" (02.11.2026): her sayfanın üst çubuğunda; tercih yalnızca cihazda. */
export function TutarGizleDugmesi() {
  const gizli = useTutarlarGizli();
  return (
    <button
      type="button"
      aria-pressed={gizli}
      aria-label={M.kabuk.menu.tutarlariGizle}
      title={M.kabuk.menu.tutarlariGizle}
      onClick={() => tutarlariGizle(!gizli)}
      className={cx(
        "inline-flex size-8 shrink-0 items-center justify-center rounded-pf border",
        gizli ? "border-accent bg-accent-soft text-accent" : "border-line text-muted hover:bg-surface-2",
      )}
    >
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
        {gizli && <path d="M4 4l16 16" />}
      </svg>
    </button>
  );
}

export interface AppShellProps {
  readonly route: Route;
  readonly children: ReactNode;
}

/** Menü grubunun zemin tonu (Gelen kararlar 01.11.2026; jetonlar `tokens.css`). */
export type Bolum = "anasayfa" | "raporlar" | "hesap" | "ayarlar";

export const NAV_GROUPS: readonly { readonly heading: string; readonly bolum: Bolum; readonly routes: readonly Route[] }[] = [
  { heading: M.kabuk.menu.anaSayfa, bolum: "anasayfa", routes: ["ana-sayfa"] },
  // Raporlar üstte (kullanıcı, 13.10.2026): okuma, yazmadan daha sık.
  { heading: M.kabuk.menu.raporlar, bolum: "raporlar", routes: ["rapor", "grafik", "donemsel-analiz"] },
  {
    heading: M.kabuk.menu.hesapIslemleri,
    bolum: "hesap",
    // Sıklık: hareket girmek > elde ne var > hesap tanımı > toplu/elle veri.
    // Sanal Hesaplar (Gelen kararlar 21.10.2026) burada, İçe aktar'ın üstünde:
    // sayfa sonuç göstermez, kural tanımlar; sonuçlar Varlık Dökümü ve Günlük
    // Analiz'de.
    routes: ["hareketler", "pozisyonlar", "hesaplar", "sanal-hesaplar", "ice-aktar", "fiyatlar", "degerleme"],
  },
  { heading: M.kabuk.menu.ayarlar, bolum: "ayarlar", routes: ["ayarlar", "izleme"] },
];

/** Sayfanın menü grubu; menüde olmayan sayfa Ana sayfa tonunda. */
export function bolumOf(route: Route): Bolum {
  return NAV_GROUPS.find((g) => g.routes.includes(route))?.bolum ?? "anasayfa";
}

/** Kasa açılınca ilk gelinen sayfalar: depolama sorusu bunlarda da uyarır (19.10.2026). */
const GIRIS_SAYFALARI: readonly Route[] = ["rapor", "hareketler", "hesaplar"];

function MenuSimgesi({ acik }: { readonly acik: boolean }) {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
      <path
        d={acik ? "M5 5l10 10M15 5L5 15" : "M3.5 5.5h13M3.5 10h13M3.5 14.5h13"}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Gruplu menü listesi — kenar gezinti ve telefondaki açılır menü aynısını çizer. */
function MenuListesi({ route, buyuk = false }: { readonly route: Route; readonly buyuk?: boolean }) {
  return (
    <ul className="flex-1 overflow-auto py-2">
      {NAV_GROUPS.map((group) => (
        <li key={group.heading} className={cx("mb-3", buyuk && "border-b border-line pb-3 last:border-0")}>
          <p
            className={cx(
              // Ana başlık: vurgu renginde; sayfalar ondan girintili ve metin
              // renginde (kullanıcı, 13.10.2026). ZEMİNSİZ (kullanıcı,
              // 15.10.2026): zemin seçili sayfanınkiyle aynıydı, karışıyordu.
              "mx-2 px-2 py-1 font-semibold uppercase tracking-wider text-accent",
              buyuk ? "py-1.5 text-[12px]" : "text-[10px]",
            )}
          >
            {group.heading}
          </p>
          <ul>
            {group.routes.map((r) => (
              <li key={r}>
                <a
                  href={routeHref(r)}
                  aria-current={route === r ? "page" : undefined}
                  className={cx(
                    "mt-0.5 block border-l-2 pr-4 pl-6",
                    buyuk ? "py-3 text-[15px]" : "py-1.5 text-[13px]",
                    route === r
                      ? "border-accent bg-[var(--pf-menu-etkin)] font-semibold text-accent"
                      : "border-transparent text-ink hover:bg-surface-2",
                  )}
                >
                  {ROUTE_TITLE[r]}
                </a>
              </li>
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

export function AppShell({ route, children }: AppShellProps) {
  const { info, isFake, repo, revision, invalidate } = useRepositoryContext();
  // Gizleme değişince sayfalar yeniden okunur ve çizilir (biçimlendirici
  // maskeyi okur); yeniden kurulmaz, açık form kaybolmaz.
  const gizli = useTutarlarGizli();
  const ilkGizli = useRef(gizli);
  useEffect(() => {
    if (ilkGizli.current === gizli) return;
    ilkGizli.current = gizli;
    invalidate();
  }, [gizli, invalidate]);
  const zemin = useZeminAyarlari(repo, revision);
  // Telefondaki menü: sayfa değişince (bağlantıya dokunuldu) ve Esc ile kapanır.
  const [menuAcik, setMenuAcik] = useState(false);
  // Menü üst çubuğun hemen altından açılır; çubuğun yüksekliği yazıya göre değişebilir.
  const ustCubuk = useRef<HTMLDivElement>(null);
  const [menuUstu, setMenuUstu] = useState(0);
  useEffect(() => setMenuAcik(false), [route]);
  useEffect(() => {
    if (!menuAcik) return undefined;
    const esc = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMenuAcik(false);
    };
    globalThis.addEventListener("keydown", esc);
    return () => globalThis.removeEventListener("keydown", esc);
  }, [menuAcik]);

  return (
    <div
      data-bolum={bolumOf(route)}
      data-menu-zemini={zemin.menu ? undefined : "kapali"}
      data-ici-zemin={zemin.ici ? undefined : "kapali"}
      className="flex min-h-dvh flex-col md:flex-row"
    >
      <a
        href="#pf-main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-pf focus:bg-surface focus:px-3 focus:py-2 focus:text-[13px]"
      >
        {M.kabuk.menu.icerigeGec}
      </a>

      {/* Kenar gezinti — geniş ekran */}
      <nav
        aria-label={M.kabuk.menu.anaGezinti}
        // SABİT (kullanıcı, 13.10.2026): sayfa kayarken menü yerinde kalır;
        // menü ekrana sığmıyorsa yalnızca kendi içinde kayar.
        className="hidden w-52 shrink-0 flex-col border-r border-line bg-surface md:sticky md:top-0 md:flex md:h-dvh"
      >
        <div className="border-b border-line px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[15px] font-semibold tracking-tight text-ink">Parafon</p>
            <div className="flex items-center gap-1.5">
              <TutarGizleDugmesi />
              <DilSecici />
            </div>
          </div>
          <p className="text-[11px] text-faint">{M.kabuk.menu.asama}</p>
        </div>
        <MenuListesi route={route} />
        <div className="border-t border-line px-4 py-2 text-[11px] text-faint">
          <p className="pf-num">{M.kabuk.menu.sema(info.schemaVersion)}</p>
          <p>{info.vfs}</p>
          {!info.persistent && <p className="text-warn">{M.kabuk.menu.kaliciDegil}</p>}
        </div>
      </nav>

      {/* Dar ekran: üst çubukta solda "Menü", sonra sayfa adı (kullanıcı kararı 04.11.2026). */}
      <div
        ref={ustCubuk}
        className="sticky top-0 z-30 flex items-center gap-2 border-b border-line bg-surface px-3 py-2 md:hidden"
      >
        <button
          type="button"
          aria-expanded={menuAcik}
          aria-controls="pf-menu"
          aria-label={menuAcik ? M.kabuk.menu.kapat : M.kabuk.menu.menu}
          onClick={() => {
            setMenuUstu(ustCubuk.current?.getBoundingClientRect().bottom ?? 0);
            setMenuAcik((x) => !x);
          }}
          className={cx(
            "-ml-1 flex shrink-0 items-center rounded-pf p-1.5",
            menuAcik ? "bg-accent-soft text-accent" : "text-ink hover:bg-surface-2",
          )}
        >
          <MenuSimgesi acik={menuAcik} />
        </button>
        <p className="min-w-0 flex-1 truncate text-[15px] text-ink">
          <span className="font-semibold text-accent">Parafon</span>
          <span className="text-faint"> · </span>
          <span className="font-semibold">{ROUTE_TITLE[route]}</span>
        </p>
        {GIRIS_SAYFALARI.includes(route) && <DepolamaUyarisi kip="ikon" />}
        <TutarGizleDugmesi />
        <DilSecici />
      </div>
      {menuAcik && (
        <nav
          id="pf-menu"
          aria-label={M.kabuk.menu.anaGezinti}
          style={{ top: menuUstu }}
          className="fixed inset-x-0 bottom-0 z-40 flex flex-col overflow-auto bg-surface pb-[env(safe-area-inset-bottom)] md:hidden"
        >
          <MenuListesi route={route} buyuk />
        </nav>
      )}

      <main id="pf-main" className="flex min-h-0 min-w-0 flex-1 flex-col bg-bg pb-[env(safe-area-inset-bottom)] md:pb-0">
        {isFake && (
          <p className="border-b border-warn-border bg-warn-soft px-4 py-1.5 text-[12px] text-warn">
            {M.kabuk.menu.sahteDepo}
          </p>
        )}
        <KilitBandi />
        <YedekBandi />
        {GIRIS_SAYFALARI.includes(route) && <DepolamaUyarisi kip="serit" />}
        {children}
      </main>
    </div>
  );
}
